import type { FormKey, ScreenKey } from "@/lib/cover-press/engine"

/**
 * One hover-card's worth of guidance. Every line is a fragment that reads on
 * from its label ("Decrease to …", "Increase to …", "Pairs with …"), so keep
 * them short and concrete: what the designer will see, not how the maths
 * works.
 */
export interface Help {
  what: string
  decrease?: string
  increase?: string
  pairs?: string
}

export const SLIDER_HELP = {
  busy: {
    what: "How many bands cut across the frame.",
    decrease: "get a few wide ribbons; 2 is one bold shape.",
    increase: "get dense contour lines.",
    pairs:
      "Ripple and Eddy read it as ring count, so their low end is a single ring.",
  },
  molten: {
    what: "How far the field is warped before it is cut into bands.",
    decrease: "calm the bands into even, parallel spacing.",
    increase: "make bands bulge and fold, pinching off islands.",
    pairs: "Contour also roughens as it rises; Ripple barely reacts.",
  },
  drift: {
    what: "Tilts the whole field top to bottom.",
    decrease: "sweep the bands up the frame.",
    increase: "sweep the bands down the frame.",
    pairs: "Sibling pressings nudge this, so 0 is the seed's own flow.",
  },
  focus: {
    what: "A lens that magnifies the field around the focal point.",
    decrease: "spread the pattern evenly edge to edge.",
    increase: "swell shapes at the focal point and crowd them away from it.",
    pairs: "Only reads as composition once the focal point sits off-centre.",
  },
  worn: {
    what: "How deep the dry-roller wear bites into each ribbon.",
    decrease: "print clean, flat ink.",
    increase: "chalk the ribbon edges until flecks reach the core.",
    pairs:
      "Nothing shows unless Ink coverage lets a lane run dry and the screen is Worn.",
  },
  lanes: {
    what: "How many roller stripes cross the frame.",
    decrease: "get a few broad dry bands.",
    increase: "get fine, frequent streaks.",
    pairs: "Roller angle sets the direction they run.",
  },
  laneCover: {
    what: "How much of the frame the dry roller reaches.",
    decrease: "confine wear to a couple of lanes.",
    increase: "let wear run across the whole pull.",
    pairs: "How worn sets the depth; this sets the reach.",
  },
  laneAngle: {
    what: "The direction the roller ran.",
    decrease: "turn the lanes back towards vertical (0°).",
    increase: "rotate them through horizontal at 90° and on to 180°.",
    pairs: "Roller lanes sets how many stripes run in that direction.",
  },
  register: {
    what: "How far the plates sit out of alignment, like a press slightly off-register.",
    decrease: "stack every plate exactly on top of the last.",
    increase: "open coloured fringes where plates overlap.",
    pairs:
      "Single-ink colourways get a pale ghost double-strike instead of fringes.",
  },
  grain: {
    what: "Paper tooth laid over the whole pull.",
    decrease: "get smooth, flat colour.",
    increase: "get visible paper texture; it also softens ink edges.",
  },
} as const satisfies Record<string, Help>

export type SliderHelpKey = keyof typeof SLIDER_HELP

export const SECTION_HELP: Record<string, Help> = {
  start: {
    what: "A whole recipe in one click: colourway, form, screen and every slider. Refine from there.",
  },
  colourway: {
    what: "A paper colour plus an ink stack. One ink prints as a duotone; two or three print as separate plates.",
  },
  plateOrder: {
    what: "Which ink prints first. Same inks, different pull order: the first plate takes the widest band and the deepest pool.",
  },
  form: {
    what: "The shape source. Contour, Current, Ripple and Eddy cut bands; Shard and Pool cut nested pools.",
  },
  focal: {
    what: "Where the lens sits on a 3×3 grid. Centre is neutral; corners pull the composition.",
  },
  impression: {
    what: "How the ink lands: the screen decides where paper punches back through, the dry roller decides where.",
  },
  paper: {
    what: "The substrate that shows through everywhere the ink is thin.",
  },
  seed: {
    what: "Every number is a different pull. The same seed always repaints identically, at any size.",
  },
  format: {
    what: "The output surface. Preview and export match pixel for pixel; CD and card back add their mask on export too.",
  },
  zoom: {
    what: "1:1 shows the export's own pixels. 200% magnifies them without smoothing, for checking wear and tooth.",
  },
  series: {
    what: "Six siblings from this recipe with a deterministic nudge to angle, drift and molten. Adopt one to make it the pull.",
  },
  inSitu: {
    what: "The same pull framed the way each surface frames it.",
  },
  pins: {
    what: "Proofs you pinned. Restore one to get its full recipe back. Pins stay in this browser only.",
  },
}

export const FORM_HELP: Record<FormKey, string> = {
  contour: "Topographic bands from a warped noise field.",
  current: "Long horizontal bands that flow like water.",
  ripple: "Concentric rings from a seeded drop point, bent by a second.",
  eddy: "One swirl wound around the centre.",
  shard: "Sharp ridged fragments, cut as pools.",
  pool: "Soft nested pools, one per plate.",
}

export const SCREEN_HELP: Record<ScreenKey, string> = {
  solid: "Flat ink, no wear.",
  wear: "Dry-roller flecks eat into the ribbons along the lanes.",
}
